/* The session's signal chips — mood, sets skipped, load down and up, records, sleep — as the task
 * cards draw them (`FeedbackSignalChips`). What is left of `feedbackRow.module.css` once the Pulpit's
 * and `/zadania`'s feedback rows were replaced by the task cards (24 September 2026). */

/* ── The signal chips ────────────────────────────────────────────────────────────────────────
 *
 * The pill itself is `shared/ui/neutralChip.module.css`, applied beside these classes; the only
 * additions here are the gap between a mark and its words, and the ink on the figure.
 *
 * The row is ONE LINE, and the reserve is exactly that: `model/sessionVerdict` caps it at three
 * chips — the mood, the dominant signal and the counter — so the height it reserves before the
 * session's second read lands is the height it ends up at. It drew five chips over two lines until
 * 16 September 2026, which is what the cap is for.
 *
 * `flex-wrap` stays as the safety net it always was. Nothing in either bundle wraps three chips at
 * the card's width, but a row that overflowed instead would clip words with no way to scroll to
 * them, and this app's rule is fit-or-scroll, never clip. `--space-6` is the chip's own height. */
.chipRow {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-6px);
  min-height: var(--space-6);
}

.chip {
  gap: var(--space-6px);
}

/* The chip's whole sentence, in one box, so the flex gap above separates the MARK from the words
 * and nothing else. */
.chipText {
  white-space: pre;
}

/* **The one run that takes the signal's colour.**
 *
 * It was the NUMERAL until 16 September 2026; the owner moved it here — a red arrow down, a green
 * arrow up, a lime trophy, with the count left grey. Two things follow. The mark is a graphic that
 * carries meaning, so it owes 3:1 rather than the 4.5:1 an 11.5px bold figure owed, which is a lower
 * bar than the arrangement it replaces. And nothing is encoded in colour alone either way: the WORD
 * says "ciężar w górę" and the arrow's DIRECTION says it again, so the row reads in greyscale.
 *
 * No alpha, for the same reason as before: it is one of the two things carrying the chip's meaning,
 * so it is drawn at the ink's full strength. */
.chipMark {
  flex: none;
  width: 12px;
  height: 12px;
  color: var(--signal-ink);
}

/* The figure stays in the chip's own `--text-2`, and only the weight sets it apart from the words.
 * It carried `--signal-ink` until 16 September 2026, when the owner moved the colour to the glyph
 * above — a grey count under a red arrow, rather than a red count beside a grey one. */
.chipValue {
  font-weight: var(--fw-extrabold);
  font-variant-numeric: tabular-nums;
}

.inkNeutral {
  --signal-ink: var(--text-2);
}

/* The `-ink` family, not the bare status tokens, and the measurement is why. On `--bg-chip` in
 * **Flame light** (#F2F1ED) the bare tokens read `--good` 1.79:1 and `--warn` 1.81:1 — a mark nobody
 * can see as a SHAPE, let alone as a colour — against 4.38 and 5.43 for their inks. `--danger` is
 * 3.01 and `--danger-ink` 3.89, and it joins them so the row has one rule rather than two.
 *
 * In **Flame Dark** every one of these three is literally the same value as its bare token
 * (8.24 / 8.15 / 4.88), so the owner's reference theme does not move by a pixel. That is what the
 * `-ink` family is FOR: the same hue, shifted per theme, which `shared/ui/EmptyState` already reads
 * for exactly this reason. Measured 16 September 2026.
 *
 * `--lime-ink` below is the one that could not follow — see its own note. */
.inkWarn {
  --signal-ink: var(--warn-ink);
}

.inkDanger {
  --signal-ink: var(--danger-ink);
}

.inkGood {
  --signal-ink: var(--good-ink);
}

/* A record is lime, everywhere a surface in this app names one — the owner's rule of 31 August
 * 2026, recorded in `pages/client-detail/AGENTS.md`. `--lime-ink` is ALREADY the per-theme ink, so
 * there is no `-ink` variant left to reach for, and it is not overridden here: it has three other
 * consumers, and darkening it would change a chart series and two client-detail surfaces nobody
 * asked to change.
 *
 * **Measured and reported, 16 September 2026: 2.99:1 on `--bg-chip` in Flame light** — 0.01 under
 * the 3:1 a meaningful graphic owes, and 12.29:1 in Flame Dark. Raised for the owner rather than
 * fixed here, because every way out of it is his call: a darker lime was already measured
 * (`#607c24`, 4.53 light) and REJECTED on 16 September because it reads 3.00 in Flame Dark. What
 * holds the row up meanwhile is that nothing is encoded in colour alone — the trophy's shape and
 * the word "rekordy" each carry the fact on their own, at `--text-2`'s 5.87:1. */
.inkRecord {
  --signal-ink: var(--lime-ink);
}

/* One skeleton chip, so the reserved row is visibly a row of chips rather than an empty band. The
 * box only: the fill and the radius are `skeleton.module.css`'s `.bar`. The specimen draws it as a
 * pill, but restating `border-radius` here would put one declaration in both a shared module and a
 * surface's own, which is settled by chunk order and differs between the dev server and the build. */
.chipHole {
  width: 104px;
  height: var(--space-6);
}

